/**
 * Map layout read straight off the streamed build record.
 *
 * The branding editors used to read a map's layout — spots, regions, teleports
 * — from the persisted `ACTIVE` map template, one row per map for the whole
 * environment. Uploading a build re-activates that row by checksum, so building
 * an older build rolls the layout back and every editor open at the time starts
 * describing a build nobody is streaming: spots of the running build read as
 * "removed", their media as "orphaned".
 *
 * This module resolves the same layout from the build the participant is
 * actually streaming (`clientParams.resources.maps[]`) and shapes it as a
 * synthetic `IMapTemplateRecord`, so the drift engine in `mapTemplateDrift`
 * keeps working unchanged.
 */
import {
  IMapTemplateRecord,
  IMapTemplateRecordRegion,
  ITemplateMeshRef,
  ITemplateSpot,
  ITemplateTeleport,
  ITemplateVec3,
} from '../interfaces/mapTemplate';

/**
 * One `clientParams.resources.maps[]` entry, as loosely as both apps type it.
 *
 * `spots` / `regions` / `teleportLocations` are `unknown[]` on purpose: the hub
 * types them that way while the admin types them fully, and this package sees
 * neither app's interfaces. They are narrowed to the raw shapes below at the
 * one point where they are read.
 */
export interface IBuildLayoutMap {
  name?: string;
  isServiceMap?: boolean | null;
  spots?: unknown[];
  regions?: unknown[];
  teleportLocations?: unknown[];
}

/**
 * The build-record fields the layout is resolved from. Structural on purpose —
 * `packages/shared` cannot see either app's `IPixelStreamingClient`, and both
 * satisfy this shape.
 */
export interface IBuildLayoutClient {
  clientId: string;
  version?: string;
  clientChangeset?: string;
  timestamp?: number;
  clientParams?: {
    version?: string;
    resources?: { maps?: IBuildLayoutMap[] } | null;
  } | null;
}

/**
 * A spot as the build metadata writes it. Builds since ~cs 12912 carry the mesh
 * data under `meshRefs`; older ones — and the type declared on the backend —
 * call it `image`. Both are read.
 */
interface IRawBuildSpot {
  id?: string;
  type?: string;
  screenshotIndex?: number;
  position?: ITemplateVec3;
  meshRefs?: ITemplateMeshRef[];
  image?: ITemplateMeshRef[];
}

/** A region as the build metadata writes it; only the read fields are spelled out. */
interface IRawBuildRegion {
  name?: string;
  spots?: string[];
}

/**
 * Tells whether a `resources.maps` entry is shaped like a map entry at all — an
 * object with fields to read, rather than a null, a bare string or a number.
 *
 * Twin of `excludeServiceResourceMaps`' helper in
 * `apps/admin/src/utils/pixelStreamingResourceMaps.ts` and of
 * `spatial-manager`'s `src/utils/ue-builds.ts`; unifying the three is a separate
 * ticket, since the admin copies are typed on an alias this package cannot see.
 *
 * @param resourceMap - Entry from the build metadata.
 * @returns True when the entry can be dereferenced.
 */
const isResourceMapObject = (resourceMap: unknown): boolean =>
  typeof resourceMap === 'object' &&
  resourceMap !== null &&
  !Array.isArray(resourceMap);

/**
 * Drops the service maps — prefab rooms, sublevels, test levels — from the
 * build's map entries.
 *
 * Must run *before* the map is picked: the pick matches by substring, and a
 * prefab path contains the content map's name, so a service entry would
 * otherwise win. `isServiceMap` is read strictly as `=== true`, so an absent
 * flag and `null` both mean a content map.
 *
 * @param resourceMaps - Map entries from the build metadata; a non-array yields
 * an empty list.
 * @returns The content entries, in their original order.
 */
const excludeServiceResourceMaps = (
  resourceMaps: IBuildLayoutMap[] | null | undefined,
): IBuildLayoutMap[] => {
  if (!Array.isArray(resourceMaps)) {
    return [];
  }

  return resourceMaps.filter(
    (resourceMap) =>
      isResourceMapObject(resourceMap) && resourceMap?.isServiceMap !== true,
  );
};

/**
 * Strips a UE asset suffix (`Map_Foo.Map_Foo` → `Map_Foo`).
 *
 * @param raw - Name or asset path segment.
 * @returns The part before the last dot, or the input when there is none.
 */
const stripUeAssetSuffix = (raw: string): string => {
  const dot = raw.lastIndexOf('.');
  if (dot <= 0) {
    return raw;
  }
  return raw.slice(0, dot);
};

/**
 * Reduces a resource-map name or a map ueIdentifier to its comparable form: the
 * last path segment, without the asset suffix, lowercased.
 *
 * @param raw - Name or asset path from either side of the comparison.
 * @returns The normalized token, empty when nothing is left to compare.
 */
const normalizeForMatch = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return '';
  }
  const lastSlash = trimmed.lastIndexOf('/');
  const basename = lastSlash >= 0 ? trimmed.slice(lastSlash + 1) : trimmed;
  return stripUeAssetSuffix(basename).toLowerCase();
};

/**
 * Tells whether a build's resource-map entry describes the given map.
 *
 * Twin of `matchesGamecastMapResourceName` in
 * `apps/admin/src/utils/fetchMapTeleportLocationsFromGamecast.ts` (and of the
 * backend's `src/utils/ue-builds.ts`) — unifying them is a separate ticket.
 *
 * @param resourceMapName - `name` of the build's map entry.
 * @param mapUeIdentifier - The map's build-stable identifier.
 * @returns True on an exact normalized match, or when either normalized form
 * contains the other.
 */
const matchesMapResourceName = (
  resourceMapName: string | undefined | null,
  mapUeIdentifier: string | undefined | null,
): boolean => {
  if (
    typeof resourceMapName !== 'string' ||
    typeof mapUeIdentifier !== 'string'
  ) {
    return false;
  }

  const normalizedName = normalizeForMatch(resourceMapName);
  const normalizedUe = normalizeForMatch(mapUeIdentifier);
  if (!normalizedName || !normalizedUe) {
    return false;
  }

  if (normalizedName === normalizedUe) {
    return true;
  }

  return (
    normalizedName.includes(normalizedUe) ||
    normalizedUe.includes(normalizedName)
  );
};

/**
 * Picks the build's resource-map entry for a map, preferring an exact name
 * match over the substring match `matchesMapResourceName` also accepts.
 *
 * The exact pass has to come first because the substring pass is symmetric and
 * order-blind: a build shipping both `Map_Museum_Night` and `Map_Museum` would
 * otherwise resolve `Map_Museum` to whichever of the two the metadata happens to
 * list first, and the whole editor would then describe the neighbouring map.
 * The same collision comes from a prefab entry (`Map_Museum_Prefab`) that
 * carries no `isServiceMap` flag — UE only started emitting it in MV-1826, so in
 * every older build `excludeServiceResourceMaps` filters nothing out.
 *
 * @param maps - Content map entries from the build metadata, service maps
 * already excluded.
 * @param ueIdentifier - The map's build-stable identifier.
 * @returns The matching entry, or `null` when no entry describes that map.
 */
const findResourceMapForUeIdentifier = (
  maps: IBuildLayoutMap[],
  ueIdentifier: string,
): IBuildLayoutMap | null => {
  const normalizedUe = normalizeForMatch(ueIdentifier);
  if (normalizedUe) {
    const exact = maps.find(
      (candidate) =>
        typeof candidate?.name === 'string' &&
        normalizeForMatch(candidate.name) === normalizedUe,
    );
    if (exact) {
      return exact;
    }
  }

  return (
    maps.find((candidate) =>
      matchesMapResourceName(candidate?.name, ueIdentifier),
    ) ?? null
  );
};

/**
 * Builds the canonical spot set of a build's map entry: the union of the flat
 * `spots[]` and the spot names listed by `regions[].spots`, deduplicated by
 * identifier, each carrying the first region that claims it.
 *
 * Twin of `buildCanonicalSpots` in
 * `apps/admin/src/utils/fetchMapTemplatesFromPixelStreaming.ts` — unifying them
 * is a separate ticket.
 *
 * @param resourceMap - Map entry from the build metadata.
 * @returns The spots, flat entries first, in metadata order.
 */
const buildCanonicalSpots = (resourceMap: IBuildLayoutMap): ITemplateSpot[] => {
  const byUe = new Map<string, ITemplateSpot>();

  const rawSpots = (resourceMap.spots ?? []) as IRawBuildSpot[];
  rawSpots.forEach((spot) => {
    if (!spot?.id) {
      return;
    }
    byUe.set(spot.id, {
      ueIdentifier: spot.id,
      type: spot.type || 'IMAGE',
      position: spot.position,
      screenshotIndex: spot.screenshotIndex,
      meshRefs: spot.meshRefs ?? spot.image,
    });
  });

  const rawRegions = (resourceMap.regions ?? []) as IRawBuildRegion[];
  rawRegions.forEach((region) => {
    (region?.spots ?? []).forEach((ueIdentifier) => {
      const existing = byUe.get(ueIdentifier);
      if (existing) {
        if (!existing.regionName) {
          existing.regionName = region.name;
        }
        return;
      }
      byUe.set(ueIdentifier, {
        ueIdentifier,
        type: 'IMAGE',
        regionName: region.name,
      });
    });
  });

  return Array.from(byUe.values());
};

/**
 * Resolves the layout of one map from the build record that ships it, shaped as
 * a synthetic `IMapTemplateRecord` the drift engine can consume unchanged.
 *
 * @param props.client - Build record being streamed.
 * @param props.mapUeIdentifier - Build-stable identifier of the map to resolve.
 * @param props.mapId - Map the layout is reported against; only carried through.
 * @returns The layout, or `null` when there is no build, no map identifier, no
 * `resources.maps`, or the build ships no entry for that map.
 */
export const resolveBuildLayout = (props: {
  client?: IBuildLayoutClient | null;
  mapUeIdentifier?: string | null;
  mapId?: string | null;
}): IMapTemplateRecord | null => {
  const { client, mapUeIdentifier, mapId } = props;
  if (!client) {
    return null;
  }

  const ueIdentifier = (mapUeIdentifier ?? '').trim();
  if (!ueIdentifier) {
    return null;
  }

  // Service maps out before the pick, not after — see excludeServiceResourceMaps.
  const maps = excludeServiceResourceMaps(client.clientParams?.resources?.maps);
  if (!maps.length) {
    return null;
  }

  const resourceMap = findResourceMapForUeIdentifier(maps, ueIdentifier);
  if (!resourceMap) {
    return null;
  }

  const spots = buildCanonicalSpots(resourceMap);
  // Regions and teleports pass through verbatim: the drift engine reads the
  // metadata's own shape (`parentTrigger` stays the object UE emits).
  const regions = (resourceMap.regions ?? []) as IMapTemplateRecordRegion[];
  const teleports = (resourceMap.teleportLocations ??
    []) as ITemplateTeleport[];

  return {
    mapTemplateId: `build:${client.clientId}`,
    mapId: mapId ?? '',
    mapName: resourceMap.name ?? '',
    ueIdentifier,
    status: 'ACTIVE',
    sourceClientId: client.clientId,
    sourceChangeset: client.clientChangeset,
    version: client.version || client.clientParams?.version,
    timestamp: client.timestamp,
    spots,
    regions,
    teleports,
    counts: {
      regions: regions.length,
      spots: spots.length,
      teleports: teleports.length,
    },
  };
};
